/**
 * PlaceOrderHandler — publishes the place-order callback onto the store so the
 * cart rail's "Place order" CTA (`StepNavSection`) can invoke it.
 *
 * Lifted out of `legacyBridge/LegacyStepShell` unchanged: the
 * routing below was already native on both paths, and the one legacy tie —
 * `usePurchaseFlowPlaceOrder().onPlaceOrder`, held in a ref and never called —
 * is gone with the bridge.
 *
 * Routing:
 *   - invoice carts → `useRevampPlaceOrder().placeOrder()`, then the dashboard.
 *   - card / SEPA   → the v2 order POST, then `stripeConfirmHandler` (published
 *     from inside the Elements scope by `RevampPaymentIntentProvider`) against
 *     the v2 intent. The sequence lives in `placeAndConfirmStripeOrder` so its
 *     ordering can be tested; read that file for why placement comes first.
 *
 * Mounted by `Summary.page` and cleared on unmount, so leaving the step
 * re-engages the rail's own fallback.
 */

import { rememberPlacedSession } from '../../drafts/placedSessions'
import { type FC, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Path } from 'constants/router/paths'
import { logger } from 'utils/logging'
import { SessionFileKind, missingFileNames, parseMissingSessionFiles } from 'models/purchaseFlow/missingSessionFiles'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { useRevampPlaceOrder } from 'dataQueries/purchaseFlowRevampPlaceOrder.query'
import { useDeleteDraft } from 'dataQueries/purchaseFlowDrafts.query'

import { PfStepPath, usePfPath } from '../../routing/pfBasePath'
import { usePfEmbed } from '../../routing/usePfEmbed'
import { usePfV2VisualUpload } from '../../fileHub'
import { deleteMissingSessionDocument } from '../../fileHub/sessionFiles.transport'
import { shootOutputLines } from '../../store/shootOutput'
import { usePfStore } from '../../store'
import { buildCartDisplay } from '../../components/cart/useCartDisplay'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { resolveTabsFromCartAndCatalogue } from '../configuration/resolveTabs'
import { planMissingFileRecovery } from './missingFileRecovery'
import { placeAndConfirmStripeOrder } from './stripePlacement'
import { useEffectivePaymentMethod, useIsChargedAtCheckout } from '../billing/useEffectivePaymentMethod'
import { orderExtras } from '../../store/stagingRenovation'

/** A failed placement is reported briefly: a retry that succeeds must not leave the error on screen. */
const PLACEMENT_ERROR_TOAST_TIMEOUT_MS = 5000

/**
 * @component PlaceOrderHandler
 * Registers the place-order handler for the lifetime of the Summary step.
 * Renders nothing.
 *
 * @example
 * <PlaceOrderHandler />
 */
export const PlaceOrderHandler: FC = () => {
  const { placeOrder: revampPlaceOrder } = useRevampPlaceOrder()
  const navigate = useNavigate()
  const trackAnalytics = usePfRevampAnalytics()
  const catalogue = usePfCatalogue().data
  const { isEmbed } = usePfEmbed()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { t } = useTranslation(['order', 'purchase_flow_revamp'])
  const pfPath = usePfPath()
  // Same gate `useResolvedStepFlow` uses, or this routes to a page the tab resolver does not render.
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)
  const { stagingEntries, deleteVisual } = usePfV2VisualUpload()
  const { mutate: deleteDraft } = useDeleteDraft()

  // Stable refs so the handler callback has an empty dep array and does
  // not reinstall itself into the store on every React Query re-render.
  const revampPlaceOrderRef = useRef(revampPlaceOrder)
  const navigateRef = useRef(navigate)
  const trackAnalyticsRef = useRef(trackAnalytics)
  const catalogueRef = useRef(catalogue)
  const spawnSuccessToastRef = useRef(spawnSuccessToast)
  const spawnErrorToastRef = useRef(spawnErrorToast)
  const tRef = useRef(t)
  const isEmbedRef = useRef(isEmbed)
  const pfPathRef = useRef(pfPath)
  const allowFallbackFlowResolverRef = useRef(allowFallbackFlowResolver)
  const stagingEntriesRef = useRef(stagingEntries)
  const deleteVisualRef = useRef(deleteVisual)
  const isChargedAtCheckout = useIsChargedAtCheckout()
  const isChargedAtCheckoutRef = useRef(isChargedAtCheckout)
  const effectivePaymentMethod = useEffectivePaymentMethod()
  const effectivePaymentMethodRef = useRef(effectivePaymentMethod)
  const deleteDraftRef = useRef(deleteDraft)

  revampPlaceOrderRef.current = revampPlaceOrder
  navigateRef.current = navigate
  trackAnalyticsRef.current = trackAnalytics
  catalogueRef.current = catalogue
  spawnSuccessToastRef.current = spawnSuccessToast
  spawnErrorToastRef.current = spawnErrorToast
  tRef.current = t
  isEmbedRef.current = isEmbed
  pfPathRef.current = pfPath
  allowFallbackFlowResolverRef.current = allowFallbackFlowResolver
  stagingEntriesRef.current = stagingEntries
  deleteVisualRef.current = deleteVisual
  isChargedAtCheckoutRef.current = isChargedAtCheckout
  effectivePaymentMethodRef.current = effectivePaymentMethod
  deleteDraftRef.current = deleteDraft

  /** `pf_revamp.order_placed` existed in the event catalogue with nothing
   *  emitting it, so a canary had no conversion signal at all (gate G5). The
   *  value is the total the client agreed to — the cart's own display total,
   *  fees included — not the Stripe gross, so invoice and card report the same
   *  number for the same cart. */
  const reportOrderPlaced = useCallback((orderId: number) => {
    const state = usePfStore.getState()
    const { total, currency } = buildCartDisplay(state.items, catalogueRef.current, orderExtras(state, catalogueRef.current))
    trackAnalyticsRef.current({
      type: PfRevampEventName.ORDER_PLACED,
      payload: {
        paymentMethod: effectivePaymentMethodRef.current ?? 'unknown',
        orderId: orderId.toString(),
        totalValue: Number(total.toString()),
        currency: currency ?? '',
        itemCount: state.items.length,
        ...(state.targetUserId !== null && {
          skipCtAutomation: state.adminSkipCtAutomation,
          skipExternalProviderSending: state.adminSkipExternalProviderSending,
        }),
      },
    })
  }, [])

  /** A placed order's draft would otherwise stay on offer under "Continue order"; its session URL reopens the order. */
  const forgetPlacedDraft = useCallback((orderId: number) => {
    const { draft, closeDraft } = usePfStore.getState()
    if (draft.sessionId) rememberPlacedSession(draft.sessionId, orderId)
    closeDraft()
    if (draft.draftId) deleteDraftRef.current(draft.draftId)
  }, [])

  /** An order exists but its payment did not go through: logged with Stripe's classification and counted. */
  const reportPaymentFailed = useCallback((orderId: number, error: unknown) => {
    const stripe = isStripeError(error) ? error : null
    const metadata = {
      orderId,
      paymentMethod: effectivePaymentMethodRef.current ?? 'unknown',
      type: stripe?.type ?? 'not_stripe',
      code: stripe?.code ?? null,
      declineCode: stripe?.decline_code ?? null,
    }
    // A decline is the client's card, not our fault; anything else points at the integration.
    const isClientSide = stripe?.type === 'card_error' || stripe?.type === 'validation_error'
    const options = { context: { component: 'PlaceOrderHandler', action: 'paymentFailed', metadata } }
    // Never the raw error: a Stripe error can carry the payment intent's client secret and the billing details.
    const detail = { message: error instanceof Error || stripe ? (error as { message?: string }).message ?? null : null }
    if (isClientSide) logger.info('pf-revamp payment refused on a placed order', detail, options)
    else logger.warn('pf-revamp payment failed on a placed order', detail, options)
    trackAnalyticsRef.current({ type: PfRevampEventName.PAYMENT_FAILED, payload: { ...metadata, orderId: orderId.toString() } })
  }, [])

  /**
   * Where a placed order leaves the client.
   *
   * Both destinations below are MAIN-APP routes, so inside the CRM iframe following them navigates
   * the frame out of Propstack with no way back — the same failure the flag-off fallback fixed. In
   * the embed the flow stays mounted and goes terminal instead; the root page then renders the
   * confirmation, which is also the only surface that can ask the parent page to close the iframe.
   */
  const settleAfterPlacement = useCallback((to: string) => {
    if (isEmbedRef.current) {
      usePfStore.getState().setOrderPlaced()
      return
    }
    navigateRef.current(to, { replace: true })
  }, [])

  /**
   * The placement refused because the session's files are no longer stored. Returns `true`
   * once handled, so the caller leaves the generic error alone. WARN not ERROR: correctly refused by a
   * healthy service. Navigates through `pfPath`, so the Propstack iframe stays in the embed.
   */
  const handleMissingSessionFiles = useCallback((error: unknown): boolean => {
    const missing = parseMissingSessionFiles(error)
    if (!missing) return false

    const state = usePfStore.getState()
    const tabs = resolveTabsFromCartAndCatalogue(
      state.items,
      catalogueRef.current?.shellProducts ?? [],
      allowFallbackFlowResolverRef.current
    )
    const { clear, forget, target } = planMissingFileRecovery(missing, state.attachments, tabs, state.productConfigs)

    // Claim AND tile, the pair the dropzone's own delete makes: the delete is what clears the session row
    // server-side, without which a retry is refused for the same file forever.
    const goneFilenames = Array.from(new Set(clear.map(({ filename }) => filename)))
    goneFilenames.forEach((filename) => {
      state.unclaimAttachment(filename)
      const entry = stagingEntriesRef.current.find((candidate) => candidate.gcFilename === filename)
      if (entry) deleteVisualRef.current(entry.id)
    })

    // A document lives in its page's configuration instead: dropping it there un-confirms the page, and
    // its session record is deleted directly, as no upload tile holds it after a resume.
    forget.forEach(({ cartItemId, flowId, next }) => state.patchConfig(cartItemId, flowId, next))
    state.markSessionFilesGone(missing.files.filter((file) => file.kind === SessionFileKind.DOCUMENT).map((file) => file.filename))
    const sessionContext = {
      getSessionId: () => usePfStore.getState().draft.sessionId,
      getWorkspaceId: () => usePfStore.getState().draft.sessionWorkspaceId,
      getEmail: () => null,
    }
    missing.files
      .filter((file) => file.kind === SessionFileKind.DOCUMENT)
      .forEach((file) => {
        deleteMissingSessionDocument(sessionContext, file).catch((error) => {
          logger.warn('pf-revamp could not delete a missing session document', error, {
            context: { component: 'PlaceOrderHandler', action: 'deleteMissingDocument', metadata: { filename: file.filename } },
          })
        })
      })

    logger.warn('pf-revamp place-order refused: session source files are no longer stored', undefined, {
      context: {
        component: 'PlaceOrderHandler',
        action: 'missingSessionFiles',
        metadata: {
          sessionId: missing.sessionId,
          missingCount: missing.files.length,
          clearedCount: clear.length,
          forgottenCount: forget.length,
          routedTo: target ? `${target.cartItemId}|${target.flowId}` : 'catalogue',
        },
      },
    })

    spawnErrorToastRef.current(
      tRef.current('order:step_validation.missing_source_files', {
        count: missing.files.length,
        files: missingFileNames(missing),
      })
    )

    navigateRef.current(
      target
        ? pfPathRef.current(`${PfStepPath.CONFIGURATION}/${target.cartItemId}/${target.flowId}`)
        : pfPathRef.current(PfStepPath.CATALOGUE)
    )

    return true
  }, [])

  const handler = useCallback(() => {
    const { isPlacingOrder, setPlacingOrder, stripeConfirmHandler } = usePfStore.getState()
    const isStripeFlow = isChargedAtCheckoutRef.current

    if (isStripeFlow) {
      if (isPlacingOrder) return

      const { setPaymentFailure } = usePfStore.getState()
      // A retry keeps the lock until its own result: the order exists whatever the retry does.
      const pendingFailure = usePfStore.getState().paymentFailure
      let placedOrderId: number | null = null
      placeAndConfirmStripeOrder({
        placeOrder: revampPlaceOrderRef.current,
        confirm: stripeConfirmHandler,
        setPlacing: setPlacingOrder,
        onPlaced: (orderId) => {
          placedOrderId = orderId
          // Settled at placement: a 3DS redirect leaves the page before `confirm` resolves, and a reload cannot
          // retry the payment (the session's intent answers 409 once ordered), so the URL reopens the order instead.
          forgetPlacedDraft(orderId)
        },
        onConfirmed: (orderId, clientSecret) => {
          setPaymentFailure(null)
          reportOrderPlaced(orderId)
          spawnSuccessToastRef.current(tRef.current('order:step_validation.order_placed.title'))
          // `PaymentStatus` reconciles by client secret, exactly as it does after
          // a Stripe redirect — `redirect: 'if_required'` means we usually get
          // here without one, so we have to supply the same params by hand.
          const search = new URLSearchParams({ dealId: orderId.toString(), payment_intent_client_secret: clientSecret })
          settleAfterPlacement(`${Path.PAYMENT_STATUS}?${search.toString()}`)
        },
        onFailed: (error, isPlaced) => {
          if (handleMissingSessionFiles(error)) return
          // Placement is idempotent on the session, so the next click re-confirms this same order.
          if (isPlaced && placedOrderId !== null) {
            reportPaymentFailed(placedOrderId, error)
            setPaymentFailure({ orderId: placedOrderId, reason: isStripeError(error) ? error.message ?? '' : '' })
            return
          }
          // A retry whose re-placement failed: the order still exists unpaid, so the lock stays and only the request failed.
          if (pendingFailure) setPaymentFailure(pendingFailure)
          spawnErrorToastRef.current(tRef.current('order:step_validation.order_error_text'), { timeout: PLACEMENT_ERROR_TOAST_TIMEOUT_MS })
        },
      })
      return
    }

    // Double-submit guard: ignore re-entry while a placement is in flight,
    // and flip the store flag so the Summary "Place order" CTA disables itself.
    if (isPlacingOrder) return
    setPlacingOrder(true)

    revampPlaceOrderRef.current()
      .then((response) => {
        logger.info('pf-revamp place-order succeeded', undefined, {
          context: {
            component: 'PlaceOrderHandler',
            action: 'placeOrder',
            metadata: { orderId: response.data.id },
          },
        })
        reportOrderPlaced(response.data.id)
        forgetPlacedDraft(response.data.id)
        spawnSuccessToastRef.current(tRef.current('order:step_validation.order_placed.title'))
        settleAfterPlacement(Path.INDEX)
      })
      .catch((error) => {
        if (handleMissingSessionFiles(error)) return
        // Lines placed without images because the shoot makes them (QA-653): a refusal of those points at the
        // frontend and backend rules disagreeing, not at the client.
        const fromShootLines = Array.from(shootOutputLines(usePfStore.getState().items, catalogueRef.current?.shellProducts).values())
        logger.error('pf-revamp place-order failed', error, {
          context: {
            component: 'PlaceOrderHandler',
            action: 'placeOrder',
            metadata: { fromShootLines },
          },
        })
        spawnErrorToastRef.current(tRef.current('order:step_validation.order_error_text'), { timeout: PLACEMENT_ERROR_TOAST_TIMEOUT_MS })
      })
      .finally(() => {
        // Re-enable the CTA. On success we've already navigated away, so this is
        // a harmless no-op there; on failure it lets the user retry.
        usePfStore.getState().setPlacingOrder(false)
      })
  // Intentional empty dep array — all values read via stable refs above
  // (`reportOrderPlaced`, `settleAfterPlacement` and `forgetPlacedDraft` are themselves dep-free and stable).
  }, [handleMissingSessionFiles, reportOrderPlaced, reportPaymentFailed, settleAfterPlacement, forgetPlacedDraft])

  useEffect(() => {
    usePfStore.getState().setPlaceOrderHandler(handler)
    return () => {
      usePfStore.getState().setPlaceOrderHandler(null)
    }
  }, [handler])

  return null
}

const isStripeError = (error: unknown): error is { type: string, message?: string, code?: string, decline_code?: string } =>
  typeof error === 'object' && error !== null && typeof (error as { type?: unknown }).type === 'string'
